import { PropsWithChildren } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaignItem } from 'api/campaigns';
import { dateToServerDateTime } from 'helpers/dates';
import { DateTime } from 'luxon';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { AuctionItem } from 'types/domain';

interface EndListingItemProps extends PropsWithChildren {
    campaignId: number;
    item: AuctionItem;
}

const EndListingItem = (props: EndListingItemProps) => {
    const { item, campaignId, children } = props;
    const toast = useToast();
    const queryClient = useQueryClient();

    const { mutate: endListing } = useMutation({
        mutationFn: () =>
            updateCampaignItem({
                campaignId,
                itemId: item.id,
                data: { end_at: dateToServerDateTime(DateTime.now()) },
            }),
        onSuccess: () => {
            toast.success('Item updated!');
            queryClient.invalidateQueries({ queryKey: campaignQueryKeys.items(campaignId) });
        },
    });

    return (
        <AlertDialog.Root type="warning">
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>End listing</AlertDialog.Title>
                        <AlertDialog.Description>
                            Are you sure you want to end this item? The status update may take up to
                            a minute to complete.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action
                            className={`disabled:cursor-not-allowed disabled:opacity-60`}
                            onClick={() => endListing()}
                        >
                            End listing
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const EndListingTrigger = AlertDialog.Trigger;

const Root = EndListingItem;
const Trigger = EndListingTrigger;

export {
    EndListingItem,
    EndListingTrigger,
    //
    Root,
    Trigger,
};
export type { EndListingItemProps };
